import { Button, useToast } from '@chakra-ui/react'
import { ChangeEvent, FC, useRef, useState } from 'react'

import useParametrizationConfigPurchase from '@hook/parametrization/use-config-purchase'

import FeriasCargaPreviewModal from '@component/Modules/ConfigPurchase/FeriasCargaPreviewModal'

import {
  FERIAS_CARGA_MAX_FILE_BYTES,
  FERIAS_CARGA_MAX_ROWS,
  FeriasCargaRow,
  parseFeriasCargaCSV
} from '../../../../../../utils/parametrization/feriasCargaCsv'

// UTF-8 estrito; se o arquivo não for UTF-8 válido, cai para Windows-1252 (padrão do Excel)
const decodeCsvBuffer = (buffer?: ArrayBuffer | null): string => {
  if (!buffer) return ''
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(buffer)
  } catch {
    return new TextDecoder('windows-1252').decode(buffer)
  }
}

const ActionFeriasCargaUpload: FC = () => {
  const { postCargaFerias } = useParametrizationConfigPurchase()
  const toast = useToast()

  const [isOpen, setIsOpen] = useState(false)
  const [rows, setRows] = useState<FeriasCargaRow[]>([])
  const [backendErrors, setBackendErrors] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (inputRef.current) inputRef.current.value = ''
    if (!file) return
    if (file.size > FERIAS_CARGA_MAX_FILE_BYTES) {
      toast({
        title: 'Arquivo muito grande',
        description: `O arquivo deve ter no máximo ${
          FERIAS_CARGA_MAX_FILE_BYTES / (1024 * 1024)
        } MB. Divida a carga em arquivos menores.`,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top'
      })
      return
    }
    const reader = new FileReader()
    reader.onerror = () => {
      toast({
        title: 'Não foi possível ler o arquivo',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top'
      })
    }
    reader.onload = ev => {
      const text = decodeCsvBuffer(ev.target?.result as ArrayBuffer)
      const { rows: parsed, parseError } = parseFeriasCargaCSV(text)
      if (parseError) {
        toast({
          title: 'Arquivo inválido',
          description: parseError,
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top'
        })
        return
      }
      if (parsed.length === 0) {
        toast({
          title: 'Nenhum registro encontrado',
          status: 'warning',
          duration: 4000,
          isClosable: true,
          position: 'top'
        })
        return
      }
      if (parsed.length > FERIAS_CARGA_MAX_ROWS) {
        toast({
          title: 'Arquivo com muitas linhas',
          description: `O arquivo tem ${parsed.length} registros; o máximo por carga é ${FERIAS_CARGA_MAX_ROWS}. Divida a carga em arquivos menores.`,
          status: 'error',
          duration: 6000,
          isClosable: true,
          position: 'top'
        })
        return
      }
      setRows(parsed)
      setBackendErrors([])
      setIsOpen(true)
    }
    // Lê os bytes: o Excel costuma salvar CSV em Windows-1252, não UTF-8
    reader.readAsArrayBuffer(file)
  }

  const handleConfirm = async (confirmedRows: FeriasCargaRow[]) => {
    if (!postCargaFerias || isLoading) return
    setIsLoading(true)
    const ferias = confirmedRows.map(r => ({
      CompradorPrincipalEmail: r.compradorPrincipalEmail.trim(),
      CompradorFeriasEmail: r.compradorFeriasEmail.trim(),
      FornecedorId: Number(r.fornecedorId),
      DataInicio: r.dataInicio,
      DataFim: r.dataFim
    }))
    const result = await postCargaFerias(ferias)
    setIsLoading(false)
    if (result.success) {
      setIsOpen(false)
      setRows([])
      setBackendErrors([])
    } else {
      setBackendErrors(result.errors)
    }
  }

  const handleClose = () => {
    setIsOpen(false)
    setRows([])
    setBackendErrors([])
  }

  return (
    <>
      <input
        type="file"
        accept=".csv"
        ref={inputRef}
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
      <Button
        size="xs"
        fontSize="12px"
        letterSpacing=".2px"
        p="4px 6px"
        bg="gray.200"
        fontWeight="400"
        _hover={{ bgColor: 'gray.100' }}
        _active={{ bgColor: 'gray.100' }}
        onClick={() => inputRef.current?.click()}
      >
        Carga
      </Button>
      <FeriasCargaPreviewModal
        isOpen={isOpen}
        initialRows={rows}
        backendErrors={backendErrors}
        onClose={handleClose}
        onConfirm={handleConfirm}
        isLoading={isLoading}
      />
    </>
  )
}

export default ActionFeriasCargaUpload
